import {ReactNode} from 'react';

export type FaqLink = {text: string; url: string};
export type FaqParagraph = string | (string | FaqLink)[];
export type FaqItem = {q: string; a: FaqParagraph[]};
export type FaqClusterKey = 'price' | 'zepbound' | 'compounded' | 'program' | 'evidence';
export type FaqCluster = {key: FaqClusterKey; label: string; items: FaqItem[]};

const PRICE_CLUSTER: FaqCluster = {
  key: 'price',
  label: 'About the price',
  items: [
    {
      q: 'What does the membership cover?',
      a: [
        'Medical screening, licensed clinician review, direct clinician messaging, and the full RISE app: Sleep Debt, Energy Schedule, Ideal Bedtime, plus medication tracking.',
      ],
    },
    {
      q: 'Why does it go up to $99/month after the first month?',
      a: [
        '$69 first month is to make starting easier and covers your asynchronous clinician review. After that, $99/month covers ongoing clinician access and treatment monitoring.',
      ],
    },
    {
      q: 'When does the charge happen?',
      a: [
        "Your first month is charged today, when you complete checkout. If a licensed clinician determines treatment isn't right for you, you're refunded in full automatically. Medication is billed separately after approval.",
      ],
    },
  ],
};

const ZEPBOUND_CLUSTER: FaqCluster = {
  key: 'zepbound',
  label: 'About Zepbound',
  items: [
    {
      q: 'What is Zepbound®?',
      a: [
        'Zepbound® (tirzepatide) is a once-weekly injection. In December 2024, it became the first FDA-approved medication for moderate-to-severe sleep apnea in adults with obesity.',
      ],
    },
    {
      q: 'What side effects should I expect, and how long do they last?',
      a: [
        [
          'The most common side effects are GI-related: nausea, reduced appetite, and fullness, typically in the first 4 to 8 weeks as your dose is titrated. Most subside as your body adjusts. Zepbound® also carries more serious risks including thyroid C-cell tumors (boxed warning), pancreatitis, gallbladder problems, and others. See the Important Safety Information below and the ',
          {text: 'full Prescribing Information', url: 'https://pi.lilly.com/us/zepbound-uspi.pdf'},
          ' before starting treatment.',
        ],
      ],
    },
    {
      q: "Who shouldn't take Zepbound®?",
      a: [
        "Zepbound® isn't right for everyone. You shouldn't take Zepbound® if you have:",
        '• A personal or family history of medullary thyroid carcinoma (MTC)',
        '• Multiple Endocrine Neoplasia syndrome type 2 (MEN 2)',
        '• A previous serious allergic reaction to tirzepatide or any ingredient in Zepbound®',
        "Zepbound® is also not recommended during pregnancy. Tell your clinician if you're pregnant, trying to become pregnant, or breastfeeding.",
        [
          "Your clinician will review your medical history during screening. If Zepbound® isn't right for you, you're refunded in full and they'll help you understand other options. See the Important Safety Information below and the ",
          {text: 'full Prescribing Information', url: 'https://pi.lilly.com/us/zepbound-uspi.pdf'},
          ' for the complete list.',
        ],
      ],
    },
  ],
};

const COMPOUNDED_CLUSTER: FaqCluster = {
  key: 'compounded',
  label: 'About compounded semaglutide',
  items: [
    {
      q: 'What is compounded semaglutide?',
      a: [
        'Compounded semaglutide is a custom-prepared version of semaglutide made by a state-licensed compounding pharmacy. It is not FDA-approved, but compounding pharmacies are state-regulated and operate under USP guidelines.',
      ],
    },
    {
      q: 'How is it delivered?',
      a: [
        'Nightly mint-flavored drops placed under your tongue. They dissolve in 30–60 seconds. No injection, no needles.',
      ],
    },
    {
      q: 'What side effects should I expect?',
      a: [
        'The most common side effects are GI-related: nausea, reduced appetite, and fullness, typically in the first weeks as your dose is titrated. Your clinician titrates to airway response, not weight loss, so you often stay at lower doses than weight-loss protocols.',
      ],
    },
  ],
};

const PROGRAM_CLUSTER: FaqCluster = {
  key: 'program',
  label: 'About the program',
  items: [
    {
      q: "What's in the RISE app I'm getting?",
      a: [
        'Full RISE app access: your personalized Sleep Debt, Energy Schedule, and Ideal Bedtime, plus medication tracking and clinician messaging built in.',
      ],
    },
    {
      q: 'How does the medication work alongside the app?',
      a: [
        'Medication addresses the physiology of your apnea. The app helps you build sleep habits around it. Together they cover both the biology and the behavior.',
      ],
    },
    {
      q: 'What if I want to cancel?',
      a: [
        "You can cancel your membership anytime from the app (no questions, no penalties). If your clinician determines treatment isn't right for you at the initial review, you're refunded automatically.",
      ],
    },
  ],
};

const EVIDENCE_CLUSTER: FaqCluster = {
  key: 'evidence',
  label: 'About the evidence',
  items: [
    {
      q: "What's the evidence for GLP-1s in sleep apnea?",
      a: [
        [
          'For Zepbound®: the SURMOUNT-OSA trial (published in The New England Journal of Medicine, 2024) showed a 55–63% reduction in apnea-hypopnea index in adults with moderate-to-severe OSA and obesity. See the ',
          {text: 'NEJM publication', url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2404881'},
          ' for the full trial data.',
        ],
        'For compounded semaglutide: there are no dedicated OSA trials. The clinical model is that semaglutide-driven weight loss reduces airway pressure and breathing interruptions (Peppard et al., Wisconsin Sleep Cohort, JAMA 2000). Individual results vary.',
      ],
    },
  ],
};

// Compounded omits the evidence cluster: its only evidence answer leads
// with Zepbound® (FDA-approved) data, and comparing compounded to an
// FDA-regulated product is a regulatory risk.
export const selectFaqClusters = (variant: 'branded' | 'compounded'): FaqCluster[] =>
  variant === 'branded'
    ? [PRICE_CLUSTER, ZEPBOUND_CLUSTER, PROGRAM_CLUSTER, EVIDENCE_CLUSTER]
    : [PRICE_CLUSTER, COMPOUNDED_CLUSTER, PROGRAM_CLUSTER];

export const renderParagraph = (paragraph: FaqParagraph, key: string | number): ReactNode => {
  if (typeof paragraph === 'string') return <p key={key}>{paragraph}</p>;
  return (
    <p key={key}>
      {paragraph.map((segment, i) =>
        typeof segment === 'string' ? (
          <span key={i}>{segment}</span>
        ) : (
          <a key={i} href={segment.url} target="_blank" rel="noopener noreferrer">
            {segment.text}
          </a>
        ),
      )}
    </p>
  );
};
